/**
 * FulfillmentCard —— 销售流程合页【履约】卡（教材 6_shipment / 资料 7_materials / 订单 8_order 三段纵排）。
 *
 * 挂在 CustomerDetail 主列 .work-content、SalesFlowProgress 进度条正下方（与 PaymentInvoiceCard 同列连排）。
 * 外壳 .flow-card（仿激励卡：品牌蓝/圆角16/留白/轻阴影），内部三段各一 SectionCard。
 *
 * 段状态徽章口径（curIdx = ORDER.indexOf(salesFlowStep)）：
 *   ORDER = ['3_contract','5_deposit','4_invoice','6_shipment','7_materials','8_order']
 *   - 段 idx < curIdx 或 salesFlowStep==='completed' → done ✅（自动收起、可点开）
 *   - 段 idx > curIdx → pending ⚪（整段置灰、父层不放操作按钮）
 *   - 段 idx === curIdx → 看本段单据细分（见各段注释）
 *
 * 就地操作：所有 mutation onSuccess 统一 invalidate ['customer-shipments'/'customer-materials'/'customer-orders', customerId]
 *   + ['customer', String(customerId)] + onRefresh() —— loading→成功提示→即时刷新、不手动刷页。
 * 角色门控：无权限操作的按钮一律不渲染（useHasPermission false → 不渲染）。
 */
import { useEffect, useState } from 'react';
import { Modal, Input, message } from 'antd';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { useHasPermission } from '../../lib/permissions';
import { Btn, Pill } from '../ui';
import { ShipmentCreateModal, MaterialCreateModal, OrderCreateModal } from '../BizCreateModals';
import { SectionCard, SectionBadge } from './SectionCard';
import './fulfillment.css';

// 段顺序：与 SalesFlowProgress ORDER_WITH/WITHOUT_DEPOSIT 同序（契约口径）
const ORDER = ['3_contract', '5_deposit', '4_invoice', '6_shipment', '7_materials', '8_order'];

interface FulfillmentCardProps {
  customerId: number;
  salesFlowStep?: string;
  depositChosen?: any;          // 当前路径相同（with/without 同序），保留契约 prop、不参与判段
  activeStepKey?: string;       // 进度条点击联动：命中本段则展开 + 滚动到位
  onRefresh?: () => void;
}

export function FulfillmentCard({
  customerId, salesFlowStep, activeStepKey, onRefresh,
}: FulfillmentCardProps) {
  const qc = useQueryClient();
  // refresh()：统一刷新本卡赖以渲染的全部 query —— 三段单据 key + 客户详情 + 父级 onRefresh。
  // 三段建单 Modal onClose、各 Inline Action（发货/签收/submit/approve/reject/waive）成功后都经此（或同 key invalidate），
  // 保证卡内徽章/单据行即时刷新、不串段（任一段操作都会带刷另两段，口径统一）。
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['customer-shipments', customerId] });
    qc.invalidateQueries({ queryKey: ['customer-materials', customerId] });
    qc.invalidateQueries({ queryKey: ['customer-orders', customerId] });
    qc.invalidateQueries({ queryKey: ['customer', String(customerId)] });
    onRefresh?.();
  };

  // 三段单据 + 收款（教材前置：需有 completed 收款单）各自查询
  const { data: shipResp } = useQuery<any>({
    queryKey: ['customer-shipments', customerId],
    queryFn: () => $.get('/shipments', { customerId, pageSize: 50 }),
  });
  const { data: matResp } = useQuery<any>({
    queryKey: ['customer-materials', customerId],
    queryFn: () => $.get('/materials', { customerId, pageSize: 50 }),
  });
  const { data: orderResp } = useQuery<any>({
    queryKey: ['customer-orders', customerId],
    queryFn: () => $.get('/orders', { customerId, pageSize: 50 }),
  });
  const { data: payResp } = useQuery<any>({
    queryKey: ['customer-payments', customerId],
    queryFn: () => $.get('/payments', { customerId, pageSize: 50 }),
  });
  // 订单段缺项预判还需合同：判 all_signed
  const { data: contractResp } = useQuery<any>({
    queryKey: ['customer-contracts', customerId],
    queryFn: () => $.get('/contracts', { customerId, pageSize: 50 }),
  });

  const shipments: any[] = shipResp?.items ?? [];
  const materials: any[] = matResp?.items ?? [];
  const orders: any[] = orderResp?.items ?? [];
  const payments: any[] = payResp?.items ?? [];
  const contracts: any[] = contractResp?.items ?? [];

  // 取最新单据行（列表按 id 倒序惯例，取首项即最新；兜底空）
  const ship = shipments[0] ?? null;
  const mat = materials[0] ?? null;
  const order = orders[0] ?? null;

  // 三段缺项预判数据源（口径对齐后端 checkCompleteness，不靠点了报错）
  const hasCompletedPayment = payments.some((p) => p.status === 'completed');
  const hasAllSignedContract = contracts.some((c) => c.esignStatus === 'all_signed');
  const hasApprovedMaterial = materials.some((m) => m.submitStatus === 'approved');

  const done = salesFlowStep === 'completed';
  const curIdx = ORDER.indexOf(salesFlowStep ?? '');

  return (
    <div className="flow-card">
      <div style={{ padding: '10px 14px 2px', fontSize: 15, fontWeight: 800, color: 'var(--text-1)' }}>
        🚚 履约
      </div>

      {/* 教材段 6_shipment */}
      <ShipmentSection
        customerId={customerId} salesFlowStep={salesFlowStep} done={done} curIdx={curIdx}
        activeStepKey={activeStepKey} ship={ship} hasCompletedPayment={hasCompletedPayment} refresh={refresh}
      />

      {/* 资料段 7_materials */}
      <MaterialSection
        customerId={customerId} salesFlowStep={salesFlowStep} done={done} curIdx={curIdx}
        activeStepKey={activeStepKey} mat={mat} refresh={refresh}
      />

      {/* 订单段 8_order */}
      <OrderSection
        customerId={customerId} salesFlowStep={salesFlowStep} done={done} curIdx={curIdx}
        activeStepKey={activeStepKey} order={order}
        hasAllSignedContract={hasAllSignedContract} hasCompletedPayment={hasCompletedPayment}
        hasApprovedMaterial={hasApprovedMaterial} refresh={refresh}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * 通用：段展开 hook —— 当前步默认展开 / done 段默认收起 / activeStepKey 命中本段展开 / 用户手动 toggle
 * ─────────────────────────────────────────────────────────── */
function useSectionExpand(
  isCurrent: boolean, stepKey: string, activeStepKey: string | undefined,
  salesFlowStep: string | undefined, customerId: number, isPending: boolean,
) {
  const [expanded, setExpanded] = useState<boolean>(isCurrent);
  // 步进 / 切客户（customerId 变）时重算默认展开（当前段展开、done 段收起）——对齐 PaymentInvoiceCard 的 salesFlowStep effect，
  // 修步进后展开口径不一致 + 跨客户串台（用户手动 toggle 的态被这次重算重置、回到默认，符合切步/切客户语义）。
  useEffect(() => {
    setExpanded(isCurrent);
    // 仅按 salesFlowStep / customerId 变化重算默认；isCurrent 随之联动、不单列依赖
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesFlowStep, customerId]);
  // activeStepKey 命中本段（进度条点击）→ 展开；★isPending（未到段、置灰）守卫：不因点击强制展开★
  useEffect(() => {
    if (activeStepKey === stepKey && !isPending) setExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepKey, stepKey]);
  return { expanded, toggle: () => setExpanded((v) => !v) };
}

// 段状态 → 是否当前进行段 / 是否未到段（置灰）的统一推导
function sectionFlags(idx: number, curIdx: number, done: boolean) {
  // done 客户全段已完成；curIdx<0（无 flow）时一律 pending 置灰
  const isCurrent = !done && curIdx >= 0 && idx === curIdx;
  const isDoneByStep = done || (curIdx >= 0 && idx < curIdx);
  const isPending = !done && (curIdx < 0 || idx > curIdx);
  return { isCurrent, isDoneByStep, isPending };
}

/* ═══════════════════════ 教材段 6_shipment ═══════════════════════ */
function ShipmentSection({
  customerId, salesFlowStep, done, curIdx, activeStepKey, ship, hasCompletedPayment, refresh,
}: {
  customerId: number; salesFlowStep?: string; done: boolean; curIdx: number;
  activeStepKey?: string; ship: any; hasCompletedPayment: boolean; refresh: () => void;
}) {
  const idx = ORDER.indexOf('6_shipment');
  const { isCurrent, isDoneByStep, isPending } = sectionFlags(idx, curIdx, done);
  const { expanded, toggle } = useSectionExpand(isCurrent, '6_shipment', activeStepKey, salesFlowStep, customerId, isPending);

  const [showCreate, setShowCreate] = useState(false);
  const canCreate = useHasPermission('shipment:create');
  const canShip = useHasPermission('shipment:ship');   // 发货+人工签收同一权限点
  const canWaive = useHasPermission('shipment:waive');

  // 徽章细分：段为当前步时看 shipment 单据状态
  let badge: SectionBadge = 'pending';
  if (isDoneByStep) badge = 'done';
  else if (isPending) badge = 'pending';
  else {
    // isCurrent：本段进行中，按 shipment 行细分
    if (!ship) badge = 'current';                                   // 无行 → 待建单
    else if (ship.status === 'delivered') badge = 'done';
    else badge = 'current';                                         // pending/shipped/in_transit 均进行中
  }
  // 已过 6 段但无 shipment 行 → 走了「无需教材」豁免（无 waived 列，靠状态机推进推断）
  const waived = isDoneByStep && !ship;

  return (
    <div id="flow-sec-6_shipment">
      <SectionCard
        title="📦 教材邮寄" badge={badge} current={isCurrent} disabled={isPending}
        expanded={expanded} onToggle={toggle}
      >
        {/* 前置硬卡明示：无 completed 收款单时，建单/发货按钮置灰 + 灰字（不报错） */}
        {!hasCompletedPayment && !isDoneByStep && (
          <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginBottom: 10 }}>
            需先收齐款才能邮寄教材
          </div>
        )}

        {waived && (
          <div style={{ fontSize: 13, color: 'var(--success-text)' }}>✅ 已申请无需教材 / 已跳过本步</div>
        )}

        {/* 已有邮寄单：显单号 + 状态 + 就地操作 */}
        {ship && (
          <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 6, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
              <Pill tone={ship.status === 'delivered' ? 'success' : 'primary'}>
                {shipStatusLabel(ship.status)}
              </Pill>
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{ship.shipmentCode}</span>
              {ship.courier && <span style={{ fontSize: 12 }}>{ship.courier} {ship.trackingNo}</span>}
            </div>
            {/* 父层 isPending(未到段) 不放操作按钮——SectionCard disabled 已置灰；此处 isPending 时不渲染按钮 */}
            {!isPending && (
              <div className="flow-row" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {/* ②发货：仅 pending 显，前置无收款则禁用 */}
                {canShip && ship.status === 'pending' && (
                  <InlineShipAction
                    customerId={customerId} ship={ship} disabled={!hasCompletedPayment} refresh={refresh}
                  />
                )}
                {/* ③人工签收：仅 shipped/in_transit 显 */}
                {canShip && ['shipped', 'in_transit'].includes(ship.status) && (
                  <InlineDeliverAction customerId={customerId} ship={ship} refresh={refresh} />
                )}
              </div>
            )}
          </div>
        )}

        {/* 建单 + 申请无需教材：仅当前/未完成段、无现单据时 */}
        {!isPending && !ship && !waived && (
          <div className="flow-row" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {canCreate && (
              <Btn kind="primary" disabled={!hasCompletedPayment} onClick={() => setShowCreate(true)}>
                + 新建邮寄单
              </Btn>
            )}
            {canWaive && (
              <InlineWaiveAction customerId={customerId} refresh={refresh} />
            )}
          </div>
        )}
      </SectionCard>

      {/* 建单 Modal（复用，受控）；关闭后刷新本卡 */}
      <ShipmentCreateModal
        open={showCreate}
        onClose={() => { setShowCreate(false); refresh(); }}
        customerId={customerId}
      />
    </div>
  );
}

// 邮寄单状态中文
function shipStatusLabel(s: string): string {
  return ({
    pending: '🔵 待发货', shipped: '🔵 已发货', in_transit: '🔵 在途',
    delivered: '✅ 已签收', exception: '🔴 异常',
  } as Record<string, string>)[s] ?? s;
}

/** 就地发货：PATCH /shipments/:id/ship 填 courier+trackingNo；invalidate 客户详情而非列表（★不用 AllListPages 的★）。 */
function InlineShipAction({
  customerId, ship, disabled, refresh,
}: { customerId: number; ship: any; disabled: boolean; refresh: () => void }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [courier, setCourier] = useState('');
  const [trackingNo, setTrackingNo] = useState('');
  const mut = useMutation({
    mutationFn: () => $.patch(`/shipments/${ship.id}/ship`, { courier: courier.trim(), trackingNo: trackingNo.trim() }),
    onSuccess: () => {
      message.success('已发货');
      setOpen(false); setCourier(''); setTrackingNo('');
      qc.invalidateQueries({ queryKey: ['customer-shipments', customerId] });
      refresh();
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '发货失败'),
  });
  return (
    <>
      <Btn kind="primary" disabled={disabled} onClick={() => setOpen(true)}>发货</Btn>
      <Modal title={`发货 · ${ship.shipmentCode ?? ''}`} open={open}
        onCancel={() => setOpen(false)}
        onOk={() => { if (!courier.trim() || !trackingNo.trim()) { message.error('快递公司和单号必填'); return; } mut.mutate(); }}
        okText={mut.isPending ? '提交中…' : '确认发货'} cancelText="取消" confirmLoading={mut.isPending}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Input placeholder="快递公司（如：顺丰 / 中通 / 圆通）" value={courier} onChange={(e) => setCourier(e.target.value)} />
          <Input placeholder="快递单号" value={trackingNo} onChange={(e) => setTrackingNo(e.target.value)} />
        </div>
      </Modal>
    </>
  );
}

/** 就地人工签收：二次确认 PATCH /shipments/:id/deliver（门控 shipment:ship）；补全后端已有但前端缺的入口。 */
function InlineDeliverAction({
  customerId, ship, refresh,
}: { customerId: number; ship: any; refresh: () => void }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => $.patch(`/shipments/${ship.id}/deliver`, {}),
    onSuccess: () => {
      message.success('已签收');
      qc.invalidateQueries({ queryKey: ['customer-shipments', customerId] });
      refresh();
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '签收失败'),
  });
  return (
    <Btn kind="default" disabled={mut.isPending} onClick={() => {
      if (mut.isPending) return;
      Modal.confirm({
        title: '标记签收？', content: `确认教材 ${ship.shipmentCode ?? ''} 已由客户签收？`,
        okText: '标记签收', cancelText: '取消', onOk: () => mut.mutateAsync(),
      });
    }}>
      {mut.isPending ? '处理中…' : '人工签收'}
    </Btn>
  );
}

/** 申请无需教材：二次确认 POST /shipments/waive（门控 shipment:waive）。 */
function InlineWaiveAction({ customerId, refresh }: { customerId: number; refresh: () => void }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => $.post('/shipments/waive', { customerId }),
    onSuccess: () => {
      message.success('已提交「无需教材」申请，等待主管审核');
      qc.invalidateQueries({ queryKey: ['customer-shipments', customerId] });
      refresh();
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '提交失败'),
  });
  return (
    <Btn kind="default" disabled={mut.isPending} onClick={() => {
      if (mut.isPending) return;
      Modal.confirm({
        title: '申请无需教材？', content: '确认该客户无需邮寄教材？提交后等待主管审核，通过即跳过本步。',
        okText: '提交申请', cancelText: '取消', onOk: () => mut.mutateAsync(),
      });
    }}>
      {mut.isPending ? '提交中…' : '申请无需教材'}
    </Btn>
  );
}

/* ═══════════════════════ 资料段 7_materials ═══════════════════════ */
function MaterialSection({
  customerId, salesFlowStep, done, curIdx, activeStepKey, mat, refresh,
}: {
  customerId: number; salesFlowStep?: string; done: boolean; curIdx: number;
  activeStepKey?: string; mat: any; refresh: () => void;
}) {
  const idx = ORDER.indexOf('7_materials');
  const { isCurrent, isDoneByStep, isPending } = sectionFlags(idx, curIdx, done);
  const { expanded, toggle } = useSectionExpand(isCurrent, '7_materials', activeStepKey, salesFlowStep, customerId, isPending);

  const [showCreate, setShowCreate] = useState(false);
  const canCreate = useHasPermission('material:create');
  const canApprove = useHasPermission('material:approve');

  // 徽章细分（当前段时按 submitStatus）
  let badge: SectionBadge = 'pending';
  let approvalNote: string | undefined;
  if (isDoneByStep) badge = 'done';
  else if (isPending) badge = 'pending';
  else {
    if (!mat) badge = 'current';                                    // 无行 → 待建档
    else if (mat.submitStatus === 'submitted') { badge = 'approval'; approvalNote = '资料已提交，等待主管审核'; }
    else if (mat.submitStatus === 'approved') badge = 'done';
    else badge = 'current';                                         // draft/in_progress/rejected
  }

  return (
    <div id="flow-sec-7_materials">
      <SectionCard
        title="📝 学员资料" badge={badge} current={isCurrent} disabled={isPending}
        expanded={expanded} onToggle={toggle} approvalNote={approvalNote}
      >
        {/* 已有资料行：显状态 + 驳回理由 + 就地操作 */}
        {mat && (
          <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 6, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
              <Pill tone={matStatusTone(mat.submitStatus)}>{matStatusLabel(mat.submitStatus)}</Pill>
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{mat.materialCode}</span>
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>完成度 {mat.completionPercentage ?? 0}%</span>
            </div>
            {mat.submitStatus === 'rejected' && mat.rejectionReason && (
              <div style={{ padding: 8, background: 'var(--danger-bg)', borderRadius: 4, fontSize: 12, marginBottom: 6 }}>
                驳回理由：{mat.rejectionReason}
              </div>
            )}
            {!isPending && (
              <div className="flow-row" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {/* ②提交：draft/in_progress/rejected 显，门控 material:create */}
                {canCreate && ['draft', 'in_progress', 'rejected'].includes(mat.submitStatus) && (
                  <InlineMaterialSubmit customerId={customerId} mat={mat} refresh={refresh} />
                )}
                {/* ③通过/驳回：submitted 显，门控 material:approve */}
                {canApprove && mat.submitStatus === 'submitted' && (
                  <>
                    <InlineMaterialApprove customerId={customerId} mat={mat} refresh={refresh} />
                    <InlineMaterialReject customerId={customerId} mat={mat} refresh={refresh} />
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* 建档：当前/未完成段、无资料行、有权限时 */}
        {!isPending && !mat && canCreate && (
          <div className="flow-row" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn kind="primary" onClick={() => setShowCreate(true)}>+ 新建学员资料</Btn>
          </div>
        )}
      </SectionCard>

      <MaterialCreateModal
        open={showCreate}
        onClose={() => { setShowCreate(false); refresh(); }}
        customerId={customerId}
      />
    </div>
  );
}

function matStatusLabel(s: string): string {
  return ({
    draft: '📝 草稿', in_progress: '📝 填写中', rejected: '🔴 已驳回',
    submitted: '🟡 待审核', approved: '✅ 已通过',
  } as Record<string, string>)[s] ?? s;
}
function matStatusTone(s: string): any {
  return ({ rejected: 'danger', submitted: 'warning', approved: 'success' } as Record<string, any>)[s] ?? 'primary';
}

/** 就地提交资料：completionStatus!=='complete' 先 toast 不打后端；二次确认 POST /materials/:id/submit。 */
function InlineMaterialSubmit({
  customerId, mat, refresh,
}: { customerId: number; mat: any; refresh: () => void }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => $.post(`/materials/${mat.id}/submit`, {}),
    onSuccess: () => {
      message.success('已提交，等待主管审核');
      qc.invalidateQueries({ queryKey: ['customer-materials', customerId] });
      refresh();
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '提交失败'),
  });
  return (
    <Btn kind="primary" disabled={mut.isPending} onClick={() => {
      if (mut.isPending) return;
      // 完成度未满先友好提示、不打后端（后端仍会硬拦、此处提前拦体验更好）
      if (mat.completionStatus !== 'complete') {
        message.error(`资料未齐全或验真未通过，完成度 ${mat.completionPercentage ?? 0}%，补齐后再提交`);
        return;
      }
      Modal.confirm({
        title: '提交资料？', content: `确认提交资料 ${mat.materialCode ?? ''} 给主管审核？`,
        okText: '提交', cancelText: '取消', onOk: () => mut.mutateAsync(),
      });
    }}>
      {mut.isPending ? '提交中…' : '提交资料'}
    </Btn>
  );
}

/** 就地通过资料：二次确认 POST /materials/:id/approve（门控 material:approve）。 */
function InlineMaterialApprove({
  customerId, mat, refresh,
}: { customerId: number; mat: any; refresh: () => void }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => $.post(`/materials/${mat.id}/approve`, {}),
    onSuccess: () => {
      message.success('已通过');
      qc.invalidateQueries({ queryKey: ['customer-materials', customerId] });
      refresh();
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '操作失败'),
  });
  return (
    <Btn kind="primary" disabled={mut.isPending} onClick={() => {
      if (mut.isPending) return;
      Modal.confirm({
        title: '通过资料？', content: `确认通过资料 ${mat.materialCode ?? ''}？`,
        okText: '通过', cancelText: '取消', onOk: () => mut.mutateAsync(),
      });
    }}>
      {mut.isPending ? '处理中…' : '✓ 通过'}
    </Btn>
  );
}

/** 就地驳回资料：TextArea 必填理由 → POST /materials/:id/reject {reason}（门控 material:approve）。 */
function InlineMaterialReject({
  customerId, mat, refresh,
}: { customerId: number; mat: any; refresh: () => void }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const mut = useMutation({
    mutationFn: () => $.post(`/materials/${mat.id}/reject`, { reason: reason.trim() }),
    onSuccess: () => {
      message.success('已驳回');
      setOpen(false); setReason('');
      qc.invalidateQueries({ queryKey: ['customer-materials', customerId] });
      refresh();
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '驳回失败'),
  });
  return (
    <>
      <Btn kind="default" onClick={() => setOpen(true)}>✗ 驳回</Btn>
      <Modal title={`驳回资料 · ${mat.materialCode ?? ''}`} open={open}
        onCancel={() => { setOpen(false); setReason(''); }}
        onOk={() => { if (!reason.trim()) { message.error('请填写驳回理由'); return; } mut.mutate(); }}
        okText={mut.isPending ? '提交中…' : '确认驳回'} okButtonProps={{ danger: true }} cancelText="取消" confirmLoading={mut.isPending}>
        <Input.TextArea autoFocus value={reason} onChange={(e) => setReason(e.target.value)} rows={4}
          placeholder="请填写驳回理由（必填），如：身份证照片模糊、学历未验真等" />
      </Modal>
    </>
  );
}

/* ═══════════════════════ 订单段 8_order ═══════════════════════ */
function OrderSection({
  customerId, salesFlowStep, done, curIdx, activeStepKey, order,
  hasAllSignedContract, hasCompletedPayment, hasApprovedMaterial, refresh,
}: {
  customerId: number; salesFlowStep?: string; done: boolean; curIdx: number;
  activeStepKey?: string; order: any;
  hasAllSignedContract: boolean; hasCompletedPayment: boolean; hasApprovedMaterial: boolean;
  refresh: () => void;
}) {
  const idx = ORDER.indexOf('8_order');
  const { isCurrent, isDoneByStep, isPending } = sectionFlags(idx, curIdx, done);
  const { expanded, toggle } = useSectionExpand(isCurrent, '8_order', activeStepKey, salesFlowStep, customerId, isPending);

  const [showCreate, setShowCreate] = useState(false);
  const canCreate = useHasPermission('order:create');

  // 徽章细分（当前段时按 approvalStatus）
  let badge: SectionBadge = 'pending';
  let approvalNote: string | undefined;
  if (isDoneByStep) badge = 'done';
  else if (isPending) badge = 'pending';
  else {
    if (!order) badge = 'current';                                 // 无行 → 待建单
    else if (order.approvalStatus === 'approved') badge = 'done';
    else if (order.approvalStatus === 'pending') { badge = 'approval'; approvalNote = '订单已提交，等待审批'; }
    else badge = 'current';                                        // none/rejected
  }

  // 缺项预判（口径对齐后端 checkCompleteness，不靠后端报错字符串）
  const missing: string[] = [];
  if (!hasAllSignedContract) missing.push('合同未签');
  if (!hasCompletedPayment) missing.push('收款未完成');
  if (!hasApprovedMaterial) missing.push('资料未审批通过');
  const ready = missing.length === 0;

  return (
    <div id="flow-sec-8_order">
      <SectionCard
        title="📦 提交订单" badge={badge} current={isCurrent} disabled={isPending}
        expanded={expanded} onToggle={toggle} approvalNote={approvalNote}
      >
        {/* 已有订单行：显状态 */}
        {order && (
          <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 6, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Pill tone={orderStatusTone(order.approvalStatus)}>{orderStatusLabel(order.approvalStatus)}</Pill>
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{order.orderCode}</span>
            </div>
          </div>
        )}

        {/* 建单：当前/未完成段、无订单（或可重提 rejected）、有权限时 */}
        {!isPending && canCreate && (!order || order.approvalStatus === 'rejected') && (
          <div className="flow-row" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {!ready ? (
              <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
                待补：{missing.join(' / ')}（齐了才能提交订单）
              </div>
            ) : (
              <Btn kind="primary" onClick={() => setShowCreate(true)}>+ 提交订单</Btn>
            )}
          </div>
        )}
      </SectionCard>

      <OrderCreateModal
        open={showCreate}
        onClose={() => { setShowCreate(false); refresh(); }}
        customerId={customerId}
      />
    </div>
  );
}

function orderStatusLabel(s: string): string {
  return ({
    none: '🔵 草稿', pending: '🟡 待审批', approved: '✅ 已通过', rejected: '🔴 已驳回',
  } as Record<string, string>)[s] ?? s;
}
function orderStatusTone(s: string): any {
  return ({ pending: 'warning', approved: 'success', rejected: 'danger' } as Record<string, any>)[s] ?? 'primary';
}
